import { useEffect, useState } from 'react';
import type { MultiFileItem } from './VideoOrderModal';

// One decode per File, shared by the selected list and the order modal.
const frameCache = new WeakMap<File, Promise<string>>();

function captureFrame(file: File): Promise<string> {
  const cached = frameCache.get(file);
  if (cached) return cached;

  const promise = new Promise<string>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    const finish = (settle: () => void) => {
      clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      settle();
    };
    const timer = setTimeout(() => finish(() => reject(new Error('thumbnail timeout'))), 8000);

    video.onloadedmetadata = () => {
      const d = Number.isFinite(video.duration) ? video.duration : 0;
      // A frame a little way in avoids the black first frame many phone clips open on.
      video.currentTime = d > 2 ? 1 : Math.max(d / 2, 0.01);
    };
    video.onseeked = () => {
      try {
        const w = 160;
        const h = Math.round((video.videoHeight / video.videoWidth) * w) || 90;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d')?.drawImage(video, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        finish(() => resolve(dataUrl));
      } catch (err) {
        finish(() => reject(err));
      }
    };
    video.onerror = () => finish(() => reject(new Error('thumbnail decode failed')));
    video.src = url;
  });

  promise.catch(() => frameCache.delete(file));
  frameCache.set(file, promise);
  return promise;
}

/** 40x40 thumbnail for a selected video, with a play tile when no frame is available. */
export function VideoThumb({ item }: { item: MultiFileItem }) {
  const localFile = item.type === 'local' ? item.file : null;
  const cloudThumb = item.type === 'cloud' ? item.file.thumbnailUrl : undefined;
  // Results are tagged with their source so a row handed a different item after a
  // reorder never shows the previous item's frame or failure.
  const [frame, setFrame] = useState<{ file: File; src: string } | null>(null);
  const [failedSrc, setFailedSrc] = useState<File | string | null>(null);

  useEffect(() => {
    if (!localFile) return;
    let cancelled = false;
    captureFrame(localFile)
      .then((src) => { if (!cancelled) setFrame({ file: localFile, src }); })
      .catch(() => { if (!cancelled) setFailedSrc(localFile); });
    return () => { cancelled = true; };
  }, [localFile]);

  const src = localFile ? (frame?.file === localFile ? frame.src : undefined) : cloudThumb;
  const failed = failedSrc !== null && (failedSrc === localFile || failedSrc === src);

  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        onError={() => setFailedSrc(src)}
        className="flex-shrink-0 object-cover bg-[#1A3A52]"
        style={{ width: 40, height: 40, borderRadius: 8 }}
      />
    );
  }

  return (
    <div
      aria-hidden="true"
      className="flex-shrink-0 flex items-center justify-center"
      style={{ width: 40, height: 40, borderRadius: 8, background: 'linear-gradient(160deg,#1A3A52,#0F4A63)' }}
    >
      <svg width="16" height="16" fill="rgba(255,255,255,.75)" viewBox="0 0 24 24">
        <path d="M8 5v14l11-7z" />
      </svg>
    </div>
  );
}
